// Collision system: cars (roof-riding / side hits / near-miss), enemies
// (combat trigger / jump-over / contact), construction sites (clear bonus /
// hit), coins and power-ups. Logic and thresholds verbatim from v1
// js/game3d.js:711-932.

import type * as THREE from 'three';
import {
  NEAR_MISS,
  CAR_LANDING_TOLERANCE,
  COIN_COLLECTION_DISTANCE,
  ROOF_RIDE_POINTS,
  CONSTRUCTION_CLEAR_POINTS,
  SLIDE,
  TRUCK,
  BARRIER,
} from '@/config/tuning';
import type { RunState } from '@/app/context';
import type { EventBus } from '@/app/events';
import type { Player } from '@/player/player';
import type { CarSystem } from '@/entities/cars';
import type { CoinSystem } from '@/entities/pickups';
import type { EnemySystem, Enemy } from '@/entities/enemies/enemy';
import type { ConstructionSystem } from '@/entities/construction';
import type { BarrierSystem } from '@/entities/barriers';
import type { PowerUpSystem } from '@/entities/powerups';
import type { PowerUpId } from '@/content/powerups';
import { addScore, incrementCombo } from '@/systems/scoring';

export interface CollisionCtx {
  run: RunState;
  bus: EventBus;
  player: Player;
  cars: CarSystem;
  coins: CoinSystem;
  enemies: EnemySystem;
  construction: ConstructionSystem;
  barriers: BarrierSystem;
  powerUps: PowerUpSystem;
  onFatalHit: () => void;
  /** Returns true if combat actually started (takes over the frame). */
  tryStartCombat: (enemy: Enemy) => boolean;
  onPowerUpCollected: (typeId: PowerUpId) => void;
  /** Paint slick: forces a lane change, no damage */
  onSlicked: () => void;
  popup: (text: string, pos: THREE.Vector3, color: string, big?: boolean) => void;
}

export function checkCollisions(dt: number, ctx: CollisionCtx): void {
  const { run, bus, player } = ctx;
  const pos = player.position;
  const isProtected = run.isInvincible || run.hitGraceTimer > 0;

  if (run.nearMissCooldown > 0) run.nearMissCooldown -= dt;

  // --- Cars (v1 :725-798) ---
  for (const car of ctx.cars.cars) {
    const cx = car.group.position.x;
    const cz = car.group.position.z;

    const overCar =
      pos.x >= cx - car.halfWidth &&
      pos.x <= cx + car.halfWidth &&
      pos.z >= cz - car.halfLength &&
      pos.z <= cz + car.halfLength;

    if (overCar) {
      if (car.underpass) {
        // Box truck (Phase 5): the trailer band catches anything that isn't
        // sliding — too high to jump, no roof to ride
        if (!player.isSliding && pos.y < TRUCK.TRAILER_TOP && !isProtected) ctx.onFatalHit();
      } else if (player.standingOnCar === car) {
        // Riding; Player.update owns the position
      } else if (player.jumpVelocity < 0 && player.prevY >= car.topHeight - CAR_LANDING_TOLERANCE) {
        // prevY catches tunnelling (v1 :756-764)
        player.landOnCar(car);
        const points = addScore(run, bus, ROOF_RIDE_POINTS);
        incrementCombo(run, bus);
        ctx.popup(`ROOF RIDE +${points}`, pos, '#7FFF00');
        bus.emit('player:roof-ride', {});
      } else if (pos.y < car.topHeight - CAR_LANDING_TOLERANCE) {
        if (!isProtected) ctx.onFatalHit();
      }
    } else if (!car.nearMissTriggered && run.nearMissCooldown <= 0) {
      const nmWidth = car.halfWidth * NEAR_MISS.ZONE_MULTIPLIER;
      const nmLength = car.halfLength * NEAR_MISS.ZONE_MULTIPLIER;
      if (
        pos.x >= cx - nmWidth &&
        pos.x <= cx + nmWidth &&
        pos.z >= cz - nmLength &&
        pos.z <= cz + nmLength &&
        pos.y <= NEAR_MISS.HEIGHT_THRESHOLD
      ) {
        car.nearMissTriggered = true;
        run.nearMissCooldown = NEAR_MISS.COOLDOWN;
        const points = addScore(run, bus, NEAR_MISS.BONUS_POINTS);
        incrementCombo(run, bus);
        ctx.popup(`NEAR MISS +${points}`, pos, '#00E5FF');
        bus.emit('player:near-miss', {});
      }
    }
  }

  // --- Enemies (v1 :799-848, Phase 5 behaviors) ---
  for (let i = ctx.enemies.enemies.length - 1; i >= 0; i--) {
    const enemy = ctx.enemies.enemies[i];
    if (!enemy || enemy.inCombat) continue;
    const epos = enemy.rig.group.position;
    const horizontal = Math.hypot(pos.x - epos.x, pos.z - epos.z);
    const enemyAirborne = enemy.airborneY > 0.3;

    // Combat trigger (v1 :819-826) — skipped while protected or sliding past
    if (!isProtected && !player.isSliding && ctx.tryStartCombat(enemy)) return;

    if (horizontal < 1.2) {
      if (player.isSliding && horizontal < SLIDE.SWEEP_RANGE && !enemyAirborne) {
        // Sweep: sliding through the enemy's legs scores like a jump-over
        const points = addScore(run, bus, enemy.def.scoreValue);
        incrementCombo(run, bus);
        ctx.popup(`SWEEP +${points}`, epos, '#B98CFF');
        bus.emit('player:swept', { enemyId: enemy.def.id });
        ctx.enemies.despawn(i);
      } else if (
        player.isJumping &&
        pos.y > 0.9 &&
        horizontal < 1.0 &&
        !enemyAirborne &&
        !enemy.blocked
      ) {
        // Jumped over the enemy (v1 :830-844); fails vs mirror-jump (jogger
        // in the air) and umbrella twirl (overhead blocked)
        const points = addScore(run, bus, enemy.def.scoreValue);
        incrementCombo(run, bus);
        ctx.popup(`JUMPED +${points}`, epos, '#FFA500');
        bus.emit('enemy:jumped', { enemyId: enemy.def.id });
        ctx.enemies.despawn(i);
      } else if (enemyAirborne && player.isJumping && horizontal < 1.0) {
        // Mid-air collision with a mirroring jogger
        if (!isProtected) ctx.onFatalHit();
      } else if (enemy.blocked && player.isJumping && pos.y > 0.9 && horizontal < 1.0) {
        // Umbrella twirl blocks the jump-over
        if (!isProtected) ctx.onFatalHit();
      } else if (horizontal < Math.max(0.6, enemy.hitRadius) && pos.y < 0.8) {
        if (!isProtected) ctx.onFatalHit();
      }
    }
  }

  // --- Paint slicks (Phase 5): no damage, forces a lane change ---
  for (const slick of ctx.enemies.slicks) {
    if (slick.triggered || player.isJumping) continue;
    if (pos.distanceTo(slick.mesh.position) < 0.7) {
      slick.triggered = true;
      ctx.onSlicked();
    }
  }

  // --- Low barriers (Phase 5): slide under, or clear with a high jump ---
  for (const barrier of ctx.barriers.barriers) {
    const bpos = barrier.group.position;
    if (Math.abs(pos.x - bpos.x) < 1.3 && Math.abs(pos.z - bpos.z) < 0.4) {
      if (!player.isSliding && pos.y > BARRIER.BAR_LOW - 0.6 && pos.y < BARRIER.BAR_HIGH) {
        if (!isProtected) ctx.onFatalHit();
      }
    }
  }

  // --- Construction sites (v1 :849-894) ---
  for (const site of ctx.construction.sites) {
    const sx = site.group.position.x;
    const sz = site.group.position.z;
    const inBounds =
      pos.x >= sx - site.width / 2 &&
      pos.x <= sx + site.width / 2 &&
      pos.z >= sz - site.depth / 2 &&
      pos.z <= sz + site.depth / 2;

    if (inBounds) {
      if (pos.y > 1.2) {
        // Clearing the site — one bonus per entry (v1 :870-880)
        if (!site.clearBonusAwarded) {
          site.clearBonusAwarded = true;
          const points = addScore(run, bus, CONSTRUCTION_CLEAR_POINTS);
          incrementCombo(run, bus);
          ctx.popup(`CLEARED +${points}`, pos, '#FFD700');
          bus.emit('construction:cleared', {});
        }
      } else if (!isProtected) {
        ctx.onFatalHit();
      }
    } else {
      site.clearBonusAwarded = false;
    }
  }

  // --- Coins (v1 :897-904, collectCoin :916-932) ---
  for (let i = ctx.coins.coins.length - 1; i >= 0; i--) {
    const coin = ctx.coins.coins[i];
    if (!coin) continue;
    if (pos.distanceTo(coin.position) < COIN_COLLECTION_DISTANCE) {
      const coinPos = coin.position.clone();
      ctx.coins.despawn(i);
      const points = addScore(run, bus, run.coinValue); // v1 COIN_POINTS, modifier-adjusted
      incrementCombo(run, bus);
      run.coinsCollected += 1;
      ctx.popup(`+${points}`, coinPos, '#FFD700');
      bus.emit('coin:collected', { total: run.coinsCollected });
    }
  }

  // --- Power-ups (v1 :906-913, collect radius 1.0) ---
  for (let i = ctx.powerUps.pickups.length - 1; i >= 0; i--) {
    const pickup = ctx.powerUps.pickups[i];
    if (!pickup) continue;
    if (pos.distanceTo(pickup.mesh.position) < 1.0) {
      const typeId = pickup.typeId;
      ctx.powerUps.despawn(i);
      ctx.onPowerUpCollected(typeId);
    }
  }
}
